import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/lib/toast";
import type { Database } from "@/integrations/supabase/types";

type Kind = Database["public"]["Enums"]["content_kind"];

const REASONS = [
  "Spam or scam",
  "Harassment or hate speech",
  "Sexually explicit",
  "Violence or threats",
  "Counterfeit / fraud",
  "Impersonation",
  "Other",
] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  targetKind: Kind;
  targetId: string;
  targetUserId?: string;
}

export function ReportDialog({ open, onClose, targetKind, targetId, targetUserId }: Props) {
  const { session } = useAuth();
  const [reason, setReason] = useState<string>(REASONS[0]);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!session?.user?.id) {
      toast.error("Sign in required");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("content_reports").insert({
      reporter_id: session.user.id,
      target_kind: targetKind,
      target_id: targetId,
      target_user_id: targetUserId ?? null,
      reason,
      details: details || null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Couldn't submit report");
      return;
    }
    toast.success("Reported. Our team responds within 24 hours.");
    setDetails("");
    setReason(REASONS[0]);
    onClose();
  };

  const inkStyle = "hsl(var(--ink))";
  const paperStyle = "hsl(var(--paper))";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-dialog-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(20, 14, 7, 0.6)",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: paperStyle,
          color: inkStyle,
          padding: "20px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)",
          borderTop: `1px solid ${inkStyle}`,
        }}
      >
        <h2
          id="report-dialog-title"
          style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 500,
            fontSize: 22,
            margin: 0,
            marginBottom: 12,
          }}
        >
          Report this
        </h2>

        <label
          style={{
            display: "block",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
            opacity: 0.7,
          }}
        >
          Reason
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${inkStyle}`,
            background: paperStyle,
            color: inkStyle,
            marginBottom: 14,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 14,
          }}
        >
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <label
          style={{
            display: "block",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
            opacity: 0.7,
          }}
        >
          Details (optional)
        </label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          disabled={busy}
          placeholder="Anything else we should know?"
          rows={3}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${inkStyle}`,
            background: paperStyle,
            color: inkStyle,
            marginBottom: 16,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            resize: "vertical",
          }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            disabled={busy}
            type="button"
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${inkStyle}`,
              background: paperStyle,
              color: inkStyle,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            type="button"
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${inkStyle}`,
              background: inkStyle,
              color: paperStyle,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Submitting…" : "Report"}
          </button>
        </div>
      </div>
    </div>
  );
}
